import React, {
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Dropdown,
  Responsive
} from 'semantic-ui-react'
// Local
import { API_URL } from '../../constant'
import { api } from '../../action/api'
import { patientRegionFilter } from './currentUserUtils'
import { RootState } from '../../store'

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

/**
 * Provides a standalone patient region select widget
 *
 * Available region filters are determined by the current user's region filter
 * user property. If the current user has a region filter property, the value
 * is used to populate the select, otherwise, regions are requested from the API
 * with the assumption that the user can see all regions
*/

type NameAndValue = {
  name: string
  value: string | null
}

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  // If true, include an 'ALL' option. Default: true
  allOption: boolean
  // Name of the patient region select widget in a form context
  name: string
  // Callback function to execute when a region is selected
  // arguments: (e, {name, value}) where value is the region alias or "" if
  // the ALL option is selected
  onChange: (
    e: any,
    nameAndValue: NameAndValue
  ) => void
  // Placeholder text. Default: Regions
  placeholder: string
  // Initial selected patient region alias
  value: any
}

function PatientRegionSelect(
  props: Props
): JSX.Element {
  const {
    allOption: hasAllOption,
    currentUser,
    name,
    onChange,
    placeholder,
    value: valueProp
  } = props

  const [
    regionsState,
    setRegionsState
  ] = useState<any[]>([])

  const hasAllOptionState = hasAllOption !== false

  useEffect(() => {
    let mounted = true
    const userRegions = patientRegionFilter(currentUser)
    if (userRegions) {
      setRegionsState(userRegions.map(region => ({
        id: region,
        alias: region,
        name: region.replaceAll('_', ' ')
      })))
    } else {
      api.get(API_URL.GET_REGIONS, {}).then(({ data }) => {
        if (!mounted) return
        setRegionsState(data as any[])
      }).catch(() => {})
    }
    return () => { mounted = false }
  }, [ currentUser ])

  const onChangeFn = (
    e: any,
    nameAndValue: NameAndValue
  ) => {
    if (onChange) {
      const nameOrPatientRegion = name || 'patientRegion'
      const val = nameAndValue.value === 'ALL'
        ? null
        : nameAndValue.value
      onChange(
        e,
        {
          name: nameOrPatientRegion,
          value: val
        }
      )
    }
  }

  const options = regionsState.map(region => ({
    key: region.alias,
    value: region.alias,
    text: region.name
  }))
  if (hasAllOptionState === true) {
    options.unshift({
      key: 'ALL',
      text: 'All',
      value: 'ALL'
    })
  }
  return (
    <Responsive
      as={ Dropdown }
      selection
      placeholder={ placeholder || 'Regions' }
      onChange={ onChangeFn }
      options={ options }
      value={ valueProp || undefined }
    />
  )
}

export default connect(mapStateToProps)(PatientRegionSelect)
